'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import { usePage } from '@/modules/pages';
import { usePageActions } from '@/modules/pages/hooks/usePageActions';
import { useDashboard } from '@/modules/dashboard';
import { useDashboardGlobalOperations } from '@/modules/global/hooks/useDashboardGlobalOperations';
import { useWebSocketContext } from '@/modules/websocket/context/WebSocketContext';
import { useNavigate } from '@/hooks/useNavigate';
import { GenericWebSocketMessage } from '@/modules/websocket/types';

type StepStatus = 'pending' | 'active' | 'completed';

interface ProgressStep {
    label: string;
    status: StepStatus;
}

const SparklesIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" stroke="#1f8894" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M5 3L5.5 5L7.5 5.5L5.5 6L5 8L4.5 6L2.5 5.5L4.5 5L5 3Z" fill="#1f8894" opacity="0.6" />
    </svg>
);

const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="9" fill="#1f8894" />
        <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SpinnerIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="animate-spin">
        <circle cx="10" cy="10" r="9" stroke="#e5e7eb" strokeWidth="2" />
        <path d="M10 1A9 9 0 0 1 19 10" stroke="#1f8894" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const PendingCircle = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="9" stroke="#d1d5db" strokeWidth="2" />
    </svg>
);

function StepIcon({ status }: { status: StepStatus }) {
    if (status === 'completed') return <CheckIcon />;
    if (status === 'active') return <SpinnerIcon />;
    return <PendingCircle />;
}

function deriveStepIndex(sessionMessages: GenericWebSocketMessage[]): number {
    let maxStep = -1;

    for (const msg of sessionMessages) {
        const data = msg.data;
        if (!data) continue;

        if (data.type === 'done') {
            return 3; // all steps complete
        }

        if (data.type === 'in-progress') {
            const agentType = data.agentMessage?.type;
            if (agentType === 'TOOL_INVOCATION' || agentType === 'TOOL_RESULT') {
                maxStep = Math.max(maxStep, 2);
            } else if (maxStep < 1) {
                maxStep = 1;
            }
        }
    }

    return maxStep;
}

function getSteps(stepIndex: number): ProgressStep[] {
    const labels = [
        'Analyzing marketing objectives',
        'Selecting optimal widgets and KPIs',
        'Arranging dashboard layout',
    ];

    return labels.map((label, i) => {
        const pos = i + 1;
        let status: StepStatus = 'pending';
        if (pos < stepIndex) status = 'completed';
        else if (pos === stepIndex) status = 'active';
        return { label, status };
    });
}

export function GeneratingPageState() {
    const { state: pageState } = usePage();
    const { setPendingNewPage, setGeneratingPage } = usePageActions();
    const { state: { currentDashboard } } = useDashboard();
    const { loadFullDashboard } = useDashboardGlobalOperations();
    const { state: wsState, clearMessages } = useWebSocketContext();
    const navigate = useNavigate();
    const doneHandledRef = useRef(false);

    const sessionId = pageState.generatingPage?.sessionId ?? null;

    const sessionMessages = useMemo(() => {
        if (!sessionId) return [];
        return wsState.messages.filter(
            (msg: GenericWebSocketMessage) => msg.action === 'create-page' && msg.data?.sessionId === sessionId
        );
    }, [wsState.messages, sessionId]);

    const stepIndex = useMemo(() => deriveStepIndex(sessionMessages), [sessionMessages]);
    const steps = useMemo(() => getSteps(stepIndex), [stepIndex]);

    // Handle done
    useEffect(() => {
        if (doneHandledRef.current) return;
        const doneMsg = sessionMessages.find((msg: GenericWebSocketMessage) => msg.data?.type === 'done');
        if (doneMsg) {
            doneHandledRef.current = true;
            const page = (doneMsg.data as any)?.page;
            if (page && currentDashboard) {
                clearMessages((msg) => !(msg.action === 'create-page' && msg.data?.sessionId === sessionId));

                loadFullDashboard(currentDashboard.id, { defaultPageId: page.id }).then(() => {
                    navigate(`/dashboard/?pageId=${page.id}`);
                    setPendingNewPage(null);

                    requestAnimationFrame(() => {
                        setGeneratingPage(null);
                    });
                });
            }
        }
    }, [sessionMessages]);

    // Handle error — go back to choice screen
    useEffect(() => {
        const errorMsg = sessionMessages.find((msg: GenericWebSocketMessage) => msg.data?.type === 'error');
        if (errorMsg) {
            setGeneratingPage(null);
            // pendingNewPage stays set, so user returns to NewPageEmptyState
        }
    }, [sessionMessages]);

    return (
        <div className="flex-1 flex items-center justify-center min-h-[60vh]">
            <div className="flex flex-col items-center max-w-md text-center">
                {/* Animated icon */}
                <div className="mb-6 animate-pulse">
                    <SparklesIcon />
                </div>

                {/* Heading */}
                <h2 className="text-2xl font-semibold text-[#2d3a3f] mb-3">
                    Generating Your Dashboard
                </h2>

                {/* Subtitle */}
                <p className="text-sm text-[#5a717a] mb-10 leading-relaxed">
                    AI is analyzing your objectives and creating a customized dashboard layout with relevant widgets and metrics.
                </p>

                {/* Progress steps */}
                <div className="flex flex-col gap-4 w-full max-w-xs">
                    {steps.map((step) => (
                        <div key={step.label} className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                                <StepIcon status={step.status} />
                            </div>
                            <span className={`text-sm text-left ${
                                step.status === 'pending' ? 'text-gray-400' :
                                step.status === 'active' ? 'text-[#2d3a3f] font-medium' :
                                'text-[#1f8894] font-medium'
                            }`}>
                                {step.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
